export const DEFAULT_POSITION_MODE = 'reverse';
export const DEFAULT_BACKTEST_QTY = 1;
export const DEFAULT_STARTING_BALANCE = 10000;
export const DEFAULT_SIZE_MODE = 'percent';
export const DEFAULT_MAX_RISK = 100;
export const DEFAULT_RISK_PERCENT = 2;
export const DEFAULT_RISK_PERCENT_BASIS = 'start';

export const POSITION_MODES = [
  {
    id: 'reverse',
    label: 'Reverse',
    description: 'Close the open trade immediately on the signal, then enter the new side.',
  },
  {
    id: 'add',
    label: 'Add only',
    description: 'Only add size when the signal matches the open side.',
    emphasis: 'Opposite signals are ignored.',
  },
  {
    id: 'reduce',
    label: 'Reduce',
    description: 'An opposite signal closes that much of the open position. It does not flip you into a new trade.',
  },
  {
    id: 'skip',
    label: 'Skip new trade',
    description: 'Stay in the open trade. Same-side and opposite signals are ignored until it exits on its own.',
  },
];

export const SIZE_MODES = [
  { id: 'percent', label: '% of account' },
  { id: 'risk', label: 'Max risk $' },
  { id: 'fixed', label: 'Fixed qty' },
];

export const RISK_PERCENT_BASES = [
  {
    id: 'start',
    label: 'Starting balance',
    description: 'Dollar risk stays fixed: this % of the starting balance, for every trade.',
  },
  {
    id: 'equity',
    label: 'Growing equity',
    description: 'Each trade risks this % of the account as it grows or shrinks after closed trades.',
  },
];

export function isPositionMode(value) {
  return POSITION_MODES.some((mode) => mode.id === value);
}

export function isSizeMode(value) {
  return SIZE_MODES.some((mode) => mode.id === value);
}

export function isRiskPercentBasis(value) {
  return RISK_PERCENT_BASES.some((basis) => basis.id === value);
}

export function parsePositiveNumber(value, fallback) {
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? n : fallback;
}

export function wholeQty(n) {
  const q = Math.floor(Number(n) + 1e-9);
  return Number.isFinite(q) && q > 0 ? q : 0;
}

export function riskPerUnit(entryPrice, stopPrice, pointValue = 1) {
  const dist = Math.abs(Number(entryPrice) - Number(stopPrice));
  const pv = Number(pointValue) > 0 ? Number(pointValue) : 1;
  if (!(dist > 0) || !Number.isFinite(dist)) return 0;
  return dist * pv;
}

export function riskDollars(qty, entryPrice, stopPrice, pointValue = 1) {
  const units = wholeQty(qty);
  const per = riskPerUnit(entryPrice, stopPrice, pointValue);
  if (!units || !(per > 0)) return 0;
  return units * per;
}

export function qtyFromFixed(qty) {
  return Math.max(1, wholeQty(qty) || 1);
}

export function qtyFromRisk({ maxRisk, entryPrice, stopPrice, pointValue } = {}) {
  const cap = Number(maxRisk);
  const per = riskPerUnit(entryPrice, stopPrice, pointValue);
  if (!(cap > 0) || !(per > 0)) return 0;
  return wholeQty(cap / per);
}

export function riskCapFromPercent({ percent, basis, startingBalance, equity } = {}) {
  const pct = Number(percent);
  if (!(pct > 0)) return 0;
  const denom = basis === 'equity' ? Number(equity) : Number(startingBalance);
  if (!(denom > 0)) return 0;
  return denom * (pct / 100);
}
